import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import { ROUTES_PATHNAME } from "../../routes/dict";
import "./Masterclasses.css";

/**
 * Las masterclass que cubren las becas del sorteo de caravanas de CVDL 2026.
 *
 * La lista viene de /info/masterclasses-becas, que lee la misma constante que
 * el script que generó los códigos. El front no filtra ni decide: si lo hiciera
 * podría mostrar una masterclass que el código de la persona no cubre, y esa
 * persona se entera recién en el checkout.
 *
 * El código llega por querystring desde el mail (?codigo=FORVET-XXXXXXXX). Se
 * muestra aca y se REENVIA al detalle del curso, que lo usa para saltear la
 * página y llevar al checkout con el descuento ya aplicado. Quien entra sin
 * código ve lo mismo y compra como siempre.
 *
 * Cada masterclass se despliega entera: título, de qué trata, y las clases que
 * incluye con su docente. Son diecisiete y hay que elegir UNA, así que el
 * criterio de decisión es el contenido, no una tarjeta con una foto. No se
 * muestran fechas: son grabadas y el día en que se dictaron no le sirve a nadie.
 */
export default function Masterclasses() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [especialidad, setEspecialidad] = useState(null);

  const codigo = (params.get("codigo") || "").trim().toUpperCase();

  useEffect(() => {
    let vivo = true;
    axios
      .get(`${process.env.REACT_APP_BACKEND_SERVER}/info/masterclasses-becas`)
      .then(({ data }) => { if (vivo) setDatos(data); })
      .catch(() => { if (vivo) setError("No pudimos cargar las masterclass. Probá de nuevo en un momento."); });
    return () => { vivo = false; };
  }, []);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(codigo);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Sin permiso de portapapeles el código está a la vista igual.
    }
  }

  const todas = useMemo(() => datos?.masterclasses || [], [datos]);

  // Las especialidades con cuántas masterclass tiene cada una, de mayor a menor.
  // El número va a la vista a propósito: con quince especialidades y diecisiete
  // masterclass, la mayoría de los filtros deja una o dos. Mostrarlo evita que
  // alguien toque y sienta que la página se vació.
  const filtros = useMemo(() => {
    const cuenta = new Map();
    todas.forEach(m => m.especialidades.forEach(e => cuenta.set(e, (cuenta.get(e) || 0) + 1)));
    return [...cuenta.entries()]
      .map(([nombre, total]) => ({ nombre, total }))
      .sort((a, b) => b.total - a.total || a.nombre.localeCompare(b.nombre, "es"));
  }, [todas]);

  const lista = especialidad
    ? todas.filter(m => m.especialidades.includes(especialidad))
    : todas;

  return (
    <MainLayout>
      <Helmet>
        <title>Masterclass | FORVET</title>
        <meta name="description" content="Elegí tu masterclass." />
      </Helmet>

      <div className="mcb-contenedor">
        {codigo && (
          <div className="mcb-premio">
            <p className="mcb-premio-titulo">Tu caravana salió sorteada</p>
            <p className="mcb-premio-texto">
              Elegí una de las masterclass y aplicá este código en el checkout:
            </p>
            <div className="mcb-codigo-fila">
              <span className="mcb-codigo">{codigo}</span>
              <button type="button" className="mcb-copiar" onClick={copiar}>
                {copiado ? "¡Copiado!" : "Copiar"}
              </button>
            </div>
       
          </div>
        )}

        <header className="mcb-encabezado">
          <h1 className="mcb-titulo">Masterclass</h1>
          <p className="mcb-bajada">
            {todas.length ? `${todas.length} masterclass grabadas, ` : ""}
            cada una con el referente de su especialidad. Elegí una y usá tu código.
          </p>
        </header>

        {filtros.length > 1 && (
          <div className="mcb-filtros" role="group" aria-label="Filtrar por especialidad">
            <button
              type="button"
              className={`mcb-filtro ${!especialidad ? "mcb-filtro-activo" : ""}`}
              aria-pressed={!especialidad}
              onClick={() => setEspecialidad(null)}
            >
              Todas <span className="mcb-filtro-num">{todas.length}</span>
            </button>

            {filtros.map(f => (
              <button
                key={f.nombre}
                type="button"
                className={`mcb-filtro ${especialidad === f.nombre ? "mcb-filtro-activo" : ""}`}
                aria-pressed={especialidad === f.nombre}
                onClick={() => setEspecialidad(especialidad === f.nombre ? null : f.nombre)}
              >
                {f.nombre} <span className="mcb-filtro-num">{f.total}</span>
              </button>
            ))}
          </div>
        )}

        {error && <p className="mcb-error">{error}</p>}

        {!datos && !error && (
          <div>{[...Array(4)].map((_, i) => <div key={i} className="mcb-esqueleto" />)}</div>
        )}

        <ol className="mcb-lista">
          {lista.map((m, i) => {
            return (
              <li key={m._id} className="mcb-item">
                <div className="mcb-item-cabecera">
                  <span className="mcb-num">{String(i + 1).padStart(2, "0")}</span>

                  <div className="mcb-item-texto">
                    <h2 className="mcb-nombre">{m.name}</h2>

                    {!!m.especialidades.length && (
                      <p className="mcb-especialidades">{m.especialidades.join(" · ")}</p>
                    )}

                    {m.description && (
                      <p className="mcb-descripcion">{m.description}</p>
                    )}

                    <p className="mcb-resumen">
                      <span className="mcb-pildora">GRABADA</span>
                      {m.cantidadClases > 0 && (
                        <span>{m.cantidadClases} clase{m.cantidadClases > 1 ? "s" : ""}</span>
                      )}
                      {!!m.docentes.length && (
                        <span>{m.docentes.length} docente{m.docentes.length > 1 ? "s" : ""}</span>
                      )}
                    </p>
                  </div>
                </div>

                {!!m.clases.length && (
                  <ol className="mcb-clases">
                    {m.clases.map((cl, j) => (
                      <li key={`${m._id}-${j}`} className="mcb-clase">
                        <span className="mcb-clase-num">{j + 1}</span>
                        <span className="mcb-clase-texto">
                          <span className="mcb-clase-nombre">{cl.nombre}</span>
                          {!!cl.docentes.length && (
                            <span className="mcb-clase-docente">{cl.docentes.join(" · ")}</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}

                <button
                  type="button"
                  className="mcb-elegir"
                  onClick={() => navigate(
                    `${ROUTES_PATHNAME.courses.href}/${m._id}` +
                    (codigo ? `?codigo=${encodeURIComponent(codigo)}` : "")
                  )}
                >
                  {codigo ? "Elegir esta masterclass" : "Ver esta masterclass"}
                </button>
              </li>
            );
          })}
        </ol>

        {datos && !lista.length && !error && (
          <p className="mcb-error">
            No hay masterclass disponibles en este momento. Escribinos y lo resolvemos.
          </p>
        )}

        {datos && datos.total !== datos.esperadas && (
          <p className="mcb-error">
            Faltan {datos.esperadas - datos.total} masterclass de la selección. Avisanos.
          </p>
        )}
      </div>
    </MainLayout>
  );
}
